import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const TARGET_DATE = new Date('2026-08-01T18:00:00');
const PIN_BYPASS = '2468';
const PIN_STORAGE_KEY = 'boostio_pin_unlock';

function useCountdown(target: Date) {
  const calc = () => {
    const diff = Math.max(0, target.getTime() - Date.now());
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    const interval = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(interval);
  }, []);
  return time;
}

/* ── Canvas particle field ── */
function ParticleCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animId: number;
    let W = 0, H = 0;
    type P = { x: number; y: number; vx: number; vy: number; r: number; alpha: number; alphaDir: number };
    let particles: P[] = [];
    const resize = () => { W = canvas.width = canvas.offsetWidth; H = canvas.height = canvas.offsetHeight; };
    const init = () => {
      particles = Array.from({ length: 80 }, () => ({
        x: Math.random() * W, y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.5 + 0.5, alpha: Math.random() * 0.5 + 0.1,
        alphaDir: Math.random() > 0.5 ? 1 : -1,
      }));
    };
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(249,115,22,${0.06 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
      particles.forEach((p) => {
        p.x += p.vx; p.y += p.vy;
        p.alpha += p.alphaDir * 0.003;
        if (p.alpha > 0.6 || p.alpha < 0.05) p.alphaDir *= -1;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(249,115,22,${p.alpha})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    resize(); init(); draw();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => { cancelAnimationFrame(animId); ro.disconnect(); };
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />;
}

/* ── Flying Rocket — launches FROM the logo icon itself ── */
function FlyingRocket({ logoRef }: { logoRef: React.RefObject<HTMLDivElement | null> }) {
  const rocketRef = useRef<HTMLDivElement>(null);
  const trailRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number>(0);

  useEffect(() => {
    const TRAIL_COUNT = 32;
    type TrailDot = { x: number; y: number; age: number };
    const trail: TrailDot[] = [];

    let rocketX = 0;
    let rocketY = 0;
    let angle = 0;

    type PathPoint = { x: number; y: number };
    let pathPoints: PathPoint[] = [];
    let pathT = 0;
    let pathSpeed = 0;
    let flying = false;

    const getRocketIconPos = () => {
      // Find the rocket icon inside the logo box
      const iconEl = document.getElementById('logo-rocket-icon');
      if (iconEl) {
        const rect = iconEl.getBoundingClientRect();
        return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      }
      const el = logoRef.current;
      if (!el) return { x: window.innerWidth / 2, y: 80 };
      const rect = el.getBoundingClientRect();
      return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    };

    const cubicBezier = (p0: number, p1: number, p2: number, p3: number, t: number) => {
      const mt = 1 - t;
      return mt * mt * mt * p0 + 3 * mt * mt * t * p1 + 3 * mt * t * t * p2 + t * t * t * p3;
    };

    const buildPath = (start: PathPoint): PathPoint[] => {
      const W = window.innerWidth;
      const H = window.innerHeight;
      // More waypoints = longer, more dramatic flight path
      const mid1 = { x: W * (0.6 + Math.random() * 0.35), y: H * (0.05 + Math.random() * 0.2) };
      const mid2 = { x: W * (0.05 + Math.random() * 0.3), y: H * (0.5 + Math.random() * 0.45) };
      const mid3 = { x: W * (0.55 + Math.random() * 0.4), y: H * (0.55 + Math.random() * 0.35) };
      const mid4 = { x: W * (0.1 + Math.random() * 0.4), y: H * (0.05 + Math.random() * 0.25) };
      const end = getRocketIconPos();
      const pts: PathPoint[] = [];
      const STEPS = 400;
      for (let i = 0; i <= STEPS; i++) {
        const t = i / STEPS;
        let x: number, y: number;
        if (t < 0.33) {
          const s = t / 0.33;
          x = cubicBezier(start.x, mid1.x, mid1.x, mid2.x, s);
          y = cubicBezier(start.y, mid1.y, mid1.y, mid2.y, s);
        } else if (t < 0.66) {
          const s = (t - 0.33) / 0.33;
          x = cubicBezier(mid2.x, mid2.x, mid3.x, mid3.x, s);
          y = cubicBezier(mid2.y, mid2.y, mid3.y, mid3.y, s);
        } else {
          const s = (t - 0.66) / 0.34;
          x = cubicBezier(mid3.x, mid4.x, end.x + (end.x - mid4.x) * 0.2, end.x, s);
          y = cubicBezier(mid3.y, mid4.y, end.y - 40, end.y, s);
        }
        pts.push({ x, y });
      }
      return pts;
    };

    const launchRocket = () => {
      if (flying) return;
      flying = true;

      const start = getRocketIconPos();
      rocketX = start.x;
      rocketY = start.y;
      pathPoints = buildPath(start);
      pathT = 0;
      pathSpeed = 0.0004;

      // Hide the logo icon
      const iconEl = document.getElementById('logo-rocket-icon');
      if (iconEl) iconEl.style.opacity = '0';

      // Show flying rocket
      const el = rocketRef.current;
      if (el) {
        el.style.left = `${rocketX}px`;
        el.style.top = `${rocketY}px`;
        el.style.opacity = '1';
      }
    };

    const tick = () => {
      frameRef.current = requestAnimationFrame(tick);
      if (!flying) return;

      const el = rocketRef.current;
      if (!el) return;

      // Speed curve: slow start → cruising → slow end
      if (pathT < 0.08) pathSpeed = 0.0004 + pathT * 0.015;
      else if (pathT > 0.88) pathSpeed = Math.max(0.0005, pathSpeed * 0.985);
      else pathSpeed = 0.0022;

      pathT = Math.min(1, pathT + pathSpeed);

      const idx = Math.min(Math.floor(pathT * (pathPoints.length - 1)), pathPoints.length - 1);
      const nextIdx = Math.min(idx + 1, pathPoints.length - 1);
      const pt = pathPoints[idx];
      const nextPt = pathPoints[nextIdx];

      rocketX = pt.x;
      rocketY = pt.y;

      // Rotate toward direction of travel (+90° offset so nose points forward)
      const dx = nextPt.x - pt.x;
      const dy = nextPt.y - pt.y;
      if (Math.abs(dx) + Math.abs(dy) > 0.05) {
        angle = Math.atan2(dy, dx) + Math.PI / 2;
      }

      el.style.left = `${rocketX}px`;
      el.style.top = `${rocketY}px`;
      el.style.transform = `translate(-50%, -50%) rotate(${angle}rad)`;

      // Trail
      trail.push({ x: rocketX, y: rocketY, age: 0 });
      if (trail.length > TRAIL_COUNT) trail.shift();
      trail.forEach((d) => { d.age += 1; });

      const trailEl = trailRef.current;
      if (trailEl) {
        trailEl.innerHTML = '';
        trail.forEach((d, i) => {
          const ratio = 1 - d.age / TRAIL_COUNT;
          const opacity = ratio * 0.9;
          const size = Math.max(1.5, 7 * ratio);
          const fireColors = ['#fde68a', '#fb923c', '#f97316', '#ea580c', '#dc2626'];
          const color = fireColors[Math.floor((1 - ratio) * (fireColors.length - 1))];
          const dot = document.createElement('div');
          dot.style.cssText = `position:absolute;left:${d.x}px;top:${d.y}px;width:${size}px;height:${size}px;border-radius:50%;background:${color};opacity:${opacity};transform:translate(-50%,-50%);filter:blur(${1.5 * (1 - ratio)}px);pointer-events:none;`;
          trailEl.appendChild(dot);
        });
      }

      // Landed back
      if (pathT >= 1) {
        flying = false;
        el.style.opacity = '0';
        trail.length = 0;
        if (trailEl) trailEl.innerHTML = '';

        // Restore logo icon with a little pop scale
        const iconEl = document.getElementById('logo-rocket-icon');
        if (iconEl) {
          iconEl.style.transition = 'opacity 0.15s ease, transform 0.3s cubic-bezier(0.34,1.56,0.64,1)';
          iconEl.style.opacity = '1';
          iconEl.style.transform = 'scale(1.5)';
          setTimeout(() => { iconEl.style.transform = 'scale(1)'; }, 50);
        }

        // Landing shockwave rings on the logo box
        const logoBoxEl = document.getElementById('logo-rocket-box');
        if (logoBoxEl) {
          for (let r = 0; r < 3; r++) {
            setTimeout(() => {
              const ring = document.createElement('div');
              ring.style.cssText = `
                position:absolute;inset:-2px;border-radius:14px;
                border:2px solid rgba(249,115,22,0.7);
                animation:landingRing 0.6s ease-out forwards;
                pointer-events:none;z-index:30;
              `;
              logoBoxEl.appendChild(ring);
              setTimeout(() => ring.remove(), 700);
            }, r * 120);
          }
        }

        // Next launch
        setTimeout(launchRocket, 4500 + Math.random() * 3000);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    const firstLaunch = setTimeout(launchRocket, 2200);

    return () => {
      cancelAnimationFrame(frameRef.current);
      clearTimeout(firstLaunch);
      // Restore icon on unmount
      const iconEl = document.getElementById('logo-rocket-icon');
      if (iconEl) iconEl.style.opacity = '1';
    };
  }, [logoRef]);

  return (
    <>
      <div ref={trailRef} className="absolute inset-0 pointer-events-none" style={{ zIndex: 20 }} />
      <div
        ref={rocketRef}
        className="absolute pointer-events-none"
        style={{
          zIndex: 21,
          opacity: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '16px',
          color: 'white',
          filter: 'drop-shadow(0 0 5px rgba(249,115,22,1)) drop-shadow(0 0 12px rgba(249,115,22,0.6))',
          transformOrigin: 'center center',
        }}
      >
        <i className="ri-rocket-2-fill" />
      </div>
    </>
  );
}

/* ── Countdown unit — subtle fade ── */
function CountdownUnit({ value, label }: { value: number; label: string }) {
  const [displayed, setDisplayed] = useState(value);
  const [fading, setFading] = useState(false);
  useEffect(() => {
    if (value !== displayed) {
      setFading(true);
      const t = setTimeout(() => { setDisplayed(value); setFading(false); }, 180);
      return () => clearTimeout(t);
    }
  }, [value, displayed]);
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative w-20 h-20 md:w-28 md:h-28">
        <div className="relative w-full h-full flex items-center justify-center rounded-2xl overflow-hidden"
          style={{ background: 'linear-gradient(145deg, #1c1c1e 0%, #111113 100%)', border: '1px solid rgba(249,115,22,0.15)' }}>
          <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-white/[0.04] to-transparent pointer-events-none" />
          <div className="absolute top-1/2 left-3 right-3 h-px bg-black/60 pointer-events-none" />
          <span className="text-3xl md:text-4xl font-black text-white tabular-nums"
            style={{ fontVariantNumeric: 'tabular-nums', opacity: fading ? 0 : 1, transform: fading ? 'translateY(-4px)' : 'translateY(0)', transition: 'opacity 0.18s ease, transform 0.18s ease' }}>
            {String(displayed).padStart(2, '0')}
          </span>
        </div>
      </div>
      <span className="text-zinc-600 text-[10px] font-bold uppercase tracking-[0.2em]">{label}</span>
    </div>
  );
}

function GridBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(to right, rgba(249,115,22,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(249,115,22,0.06) 1px, transparent 1px)`, backgroundSize: '64px 64px' }} />
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-transparent to-zinc-950" />
      <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-transparent to-zinc-950" />
    </div>
  );
}

function NoiseOverlay() {
  return (
    <div className="absolute inset-0 pointer-events-none opacity-[0.025]"
      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`, backgroundRepeat: 'repeat', backgroundSize: '128px 128px' }} />
  );
}

function Orbs() {
  return (
    <>
      <div className="absolute rounded-full pointer-events-none" style={{ width: '700px', height: '700px', top: '50%', left: '50%', background: 'radial-gradient(circle, rgba(249,115,22,0.07) 0%, transparent 65%)', animation: 'orbPulse 12s ease-in-out infinite' }} />
      <div className="absolute rounded-full pointer-events-none" style={{ width: '350px', height: '350px', top: '10%', right: '5%', background: 'radial-gradient(circle, rgba(249,115,22,0.1) 0%, transparent 65%)', animation: 'orbDrift 8s ease-in-out infinite' }} />
      <div className="absolute rounded-full pointer-events-none" style={{ width: '250px', height: '250px', bottom: '10%', left: '5%', background: 'radial-gradient(circle, rgba(249,115,22,0.08) 0%, transparent 65%)', animation: 'orbDrift 11s ease-in-out infinite reverse' }} />
    </>
  );
}

function Rings() {
  return (
    <>
      {[{ size: 480, speed: '28s', opacity: 0.07, dash: true }, { size: 680, speed: '45s', opacity: 0.04, dash: false }, { size: 900, speed: '70s', opacity: 0.025, dash: true }].map((ring, i) => (
        <div key={i} className="absolute pointer-events-none rounded-full" style={{ width: ring.size, height: ring.size, top: '50%', left: '50%', marginTop: -ring.size / 2, marginLeft: -ring.size / 2, border: `1px ${ring.dash ? 'dashed' : 'solid'} rgba(249,115,22,0.6)`, opacity: ring.opacity, animation: `rotateSlow ${ring.speed} linear infinite ${i % 2 === 1 ? 'reverse' : ''}` }} />
      ))}
    </>
  );
}

function ScanLine() {
  return (
    <div className="absolute left-0 right-0 h-[2px] pointer-events-none" style={{ background: 'linear-gradient(to right, transparent, rgba(249,115,22,0.4) 30%, rgba(249,115,22,0.6) 50%, rgba(249,115,22,0.4) 70%, transparent)', animation: 'scanLine 10s linear infinite', filter: 'blur(1px)' }} />
  );
}

function Sparks() {
  const sparks = [
    { top: '8%', left: '6%', size: 2, delay: '0s', dur: '5s' },
    { top: '22%', left: '91%', size: 1.5, delay: '1.3s', dur: '4s' },
    { top: '55%', left: '3%', size: 2.5, delay: '0.7s', dur: '6s' },
    { top: '78%', left: '94%', size: 1.5, delay: '2.1s', dur: '5s' },
    { top: '35%', left: '97%', size: 2, delay: '0.4s', dur: '7s' },
    { top: '88%', left: '12%', size: 1, delay: '1.9s', dur: '4.5s' },
    { top: '15%', left: '75%', size: 1.5, delay: '1s', dur: '6.5s' },
    { top: '65%', left: '1%', size: 2, delay: '1.6s', dur: '5.5s' },
  ];
  return (
    <>
      {sparks.map((s, i) => (
        <div key={i} className="absolute rounded-full pointer-events-none" style={{ top: s.top, left: s.left, width: s.size, height: s.size, background: 'rgba(249,115,22,0.7)', boxShadow: '0 0 4px rgba(249,115,22,0.8)', animation: `sparkFloat ${s.dur} ease-in-out infinite`, animationDelay: s.delay }} />
      ))}
    </>
  );
}

function PinUnlock() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setPin('');
      setError(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const submit = (value: string) => {
    if (value === PIN_BYPASS) {
      try { localStorage.setItem(PIN_STORAGE_KEY, '1'); } catch { /* ignore */ }
      navigate('/', { replace: true });
      window.location.reload();
    } else {
      setError(true);
      setPin('');
      setTimeout(() => setError(false), 600);
    }
  };

  const handleChange = (v: string) => {
    const clean = v.replace(/\D/g, '').slice(0, 4);
    setPin(clean);
    if (clean.length === 4) submit(clean);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Prihlásiť sa cez PIN"
        className="absolute top-5 right-5 z-30 w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900/70 border border-zinc-800 text-zinc-500 hover:text-orange-400 hover:border-orange-500/40 transition-colors backdrop-blur-sm"
      >
        <i className="ri-lock-line text-base" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-6"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-sm rounded-2xl p-8"
            style={{
              background: 'linear-gradient(145deg, #1c1c1e 0%, #111113 100%)',
              border: '1px solid rgba(249,115,22,0.25)',
              boxShadow: '0 30px 80px -20px rgba(0,0,0,0.8), 0 0 40px rgba(249,115,22,0.15)',
              animation: 'fadeSlideUp 0.3s cubic-bezier(0.4,0,0.2,1) both',
            }}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Zavrieť"
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-white transition-colors"
            >
              <i className="ri-close-line text-lg" />
            </button>

            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-xl bg-orange-500/10 border border-orange-500/30">
                <i className="ri-shield-keyhole-line text-orange-400 text-xl" />
              </div>
              <h2 className="text-white text-xl font-bold mb-1.5">Vstup pre oprávnených</h2>
              <p className="text-zinc-500 text-sm mb-6">Zadajte 4-miestny PIN.</p>

              <input
                ref={inputRef}
                type="password"
                inputMode="numeric"
                pattern="\d*"
                autoComplete="off"
                value={pin}
                onChange={(e) => handleChange(e.target.value)}
                maxLength={4}
                className="w-full text-center text-3xl font-bold tracking-[1em] text-white bg-zinc-900 rounded-xl py-4 outline-none transition-colors"
                style={{
                  border: `1px solid ${error ? 'rgba(239,68,68,0.6)' : 'rgba(249,115,22,0.2)'}`,
                  paddingLeft: '1em',
                  animation: error ? 'shake 0.4s ease-in-out' : undefined,
                }}
              />

              <p className="text-zinc-700 text-xs mt-5">
                {error ? <span className="text-red-400">Nesprávny PIN</span> : 'Po zadaní PINu vstúpite na web'}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function UzCoskoro() {
  const [visible, setVisible] = useState(false);
  const countdown = useCountdown(TARGET_DATE);
  const logoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700;800&display=swap');
        @keyframes orbPulse { 0%,100%{transform:translate(-50%,-50%) scale(1);opacity:.8} 50%{transform:translate(-50%,-50%) scale(1.1);opacity:1} }
        @keyframes orbDrift { 0%,100%{transform:scale(1) translateY(0px)} 50%{transform:scale(1.15) translateY(-25px)} }
        @keyframes rotateSlow { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes scanLine { 0%{top:-4px;opacity:0} 3%{opacity:1} 97%{opacity:1} 100%{top:100%;opacity:0} }
        @keyframes sparkFloat { 0%,100%{transform:translateY(0px) scale(1);opacity:.5} 33%{transform:translateY(-14px) scale(1.4);opacity:1} 66%{transform:translateY(-6px) scale(.8);opacity:.7} }
        @keyframes pulseRing { 0%{transform:scale(1);opacity:.5} 100%{transform:scale(2.4);opacity:0} }
        @keyframes shimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }
        @keyframes fadeSlideUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes borderGlow { 0%,100%{border-color:rgba(249,115,22,0.15)} 50%{border-color:rgba(249,115,22,0.4)} }
        @keyframes landingRing {
          0%   { transform: scale(1);   opacity: 0.8; }
          100% { transform: scale(2.2); opacity: 0; }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
        .font-space { font-family: 'Space Grotesk', sans-serif; }
        .shimmer-text {
          background: linear-gradient(90deg, #fb923c 0%, #fde68a 30%, #f97316 50%, #fde68a 70%, #fb923c 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 4s linear infinite;
        }
        .feature-card { animation: borderGlow 4s ease-in-out infinite; }
      `}</style>

      <div className="font-space min-h-screen bg-zinc-950 flex flex-col items-center justify-center px-6 py-16 relative overflow-hidden">

        <GridBackground />
        <NoiseOverlay />
        <ParticleCanvas />
        <Orbs />
        <Rings />
        <ScanLine />
        <Sparks />

        {/* Flying rocket layer — rendered over everything */}
        <FlyingRocket logoRef={logoRef} />

        {/* PIN unlock — owner login */}
        <PinUnlock />

        {/* Edge lines */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-500/20 to-transparent" />

        {/* Corner accents */}
        <div className="absolute top-6 left-6 w-8 h-8 border-t border-l border-orange-500/30 pointer-events-none" />
        <div className="absolute top-6 right-6 w-8 h-8 border-t border-r border-orange-500/30 pointer-events-none" />
        <div className="absolute bottom-6 left-6 w-8 h-8 border-b border-l border-orange-500/30 pointer-events-none" />
        <div className="absolute bottom-6 right-6 w-8 h-8 border-b border-r border-orange-500/30 pointer-events-none" />

        {/* Logo */}
        <div
          ref={logoRef}
          className="mb-14 relative z-10"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(-16px)', transition: 'all 0.7s cubic-bezier(0.4,0,0.2,1)' }}
        >
          <Link to="/" className="flex items-center gap-2.5 cursor-pointer group">
            <div id="logo-rocket-box" className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-xl group-hover:bg-orange-400 transition-all duration-200 group-hover:scale-105 relative"
              style={{ boxShadow: '0 0 20px rgba(249,115,22,0.4)' }}>
              <i id="logo-rocket-icon" className="ri-rocket-2-fill text-white text-base" />
            </div>
            <span className="text-white font-bold text-2xl tracking-tight">
              boost<span className="text-orange-500">io</span>
            </span>
          </Link>
        </div>

        {/* Main content */}
        <div className="w-full max-w-2xl text-center relative z-10">

          {/* Badge */}
          <div className="inline-flex items-center justify-center mb-10 relative"
            style={{ opacity: visible ? 1 : 0, animation: visible ? 'fadeSlideUp 0.7s cubic-bezier(0.4,0,0.2,1) 0.1s both' : 'none' }}>
            <div className="absolute inset-0 rounded-full border border-orange-500/25" style={{ animation: 'pulseRing 2.8s ease-out infinite' }} />
            <div className="absolute inset-0 rounded-full border border-orange-500/15" style={{ animation: 'pulseRing 2.8s ease-out infinite', animationDelay: '0.9s' }} />
            <div className="relative inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/25 text-orange-400 text-xs font-semibold px-5 py-2.5 rounded-full backdrop-blur-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" style={{ animation: 'sparkFloat 2s ease-in-out infinite' }} />
              Niečo skvelé sa chystá
            </div>
          </div>

          {/* Heading */}
          <div style={{ opacity: visible ? 1 : 0, animation: visible ? 'fadeSlideUp 0.7s cubic-bezier(0.4,0,0.2,1) 0.2s both' : 'none' }}>
            <h1 className="text-5xl md:text-7xl font-black text-white mb-5 leading-[1.05] tracking-tight">
              Už <span className="shimmer-text">čoskoro</span>
            </h1>
          </div>

          {/* Countdown */}
          <div className="flex items-start justify-center gap-3 md:gap-5 mb-14"
            style={{ opacity: visible ? 1 : 0, animation: visible ? 'fadeSlideUp 0.7s cubic-bezier(0.4,0,0.2,1) 0.35s both' : 'none' }}>
            <CountdownUnit value={countdown.days} label="Dní" />
            <div className="text-zinc-700 text-2xl font-black mt-8 select-none">:</div>
            <CountdownUnit value={countdown.hours} label="Hodín" />
            <div className="text-zinc-700 text-2xl font-black mt-8 select-none">:</div>
            <CountdownUnit value={countdown.minutes} label="Minút" />
            <div className="text-zinc-700 text-2xl font-black mt-8 select-none">:</div>
            <CountdownUnit value={countdown.seconds} label="Sekúnd" />
          </div>

          {/* Contact */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-5 mb-10"
            style={{ opacity: visible ? 1 : 0, animation: visible ? 'fadeSlideUp 0.7s cubic-bezier(0.4,0,0.2,1) 0.5s both' : 'none' }}>
            <a href="mailto:info@boostio.sk" className="flex items-center gap-2 text-zinc-500 hover:text-orange-400 text-sm transition-all duration-200 cursor-pointer group">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-zinc-900 border border-zinc-800 group-hover:border-orange-500/30 transition-colors duration-200">
                <i className="ri-mail-line text-xs" />
              </div>
              info@boostio.sk
            </a>
            <div className="w-px h-4 bg-zinc-800 hidden sm:block" />
            <a href="https://wa.me/421905929167" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 text-zinc-500 hover:text-orange-400 text-sm transition-all duration-200 cursor-pointer group">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-zinc-900 border border-zinc-800 group-hover:border-orange-500/30 transition-colors duration-200">
                <i className="ri-whatsapp-line text-xs" />
              </div>
              +421 905 929 167
            </a>
          </div>

          {/* Footer */}
          <p className="text-zinc-700 text-xs"
            style={{ opacity: visible ? 1 : 0, animation: visible ? 'fadeSlideUp 0.7s cubic-bezier(0.4,0,0.2,1) 0.65s both' : 'none' }}>
            © {new Date().getFullYear()} Boostio · Kamil Frajtko
          </p>
        </div>
      </div>
    </>
  );
}
